/**
 * Pure helpers for admin clinic price grids (TM / TC catalog trees).
 * Keeps tree logic out of pages so layout/components can evolve independently.
 */

const MAX_DEPTH = 20;

/**
 * @param {Array<{ _id: string, uid?: number, label?: string, name?: string, children?: unknown[] }>} categories
 * @param {string} parentPath
 * @param {number} level
 * @returns {Array<{ id: string, uid?: number, label: string, path: string, level: number, isLeaf: boolean, isHeader: boolean, children: unknown[] }>}
 */
export function flattenCategories(categories, parentPath = "", level = 0) {
  if (level > MAX_DEPTH) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(`Category depth exceeded ${MAX_DEPTH}, truncating at level ${level}`);
    }
    return [];
  }

  let result = [];
  (categories || []).forEach((category) => {
    const fullPath = parentPath ? `${parentPath} > ${category.label}` : category.label;
    const hasChildren = category.children && category.children.length > 0;

    result.push({
      id: category._id,
      uid: category.uid,
      label: category.label || category.name,
      path: fullPath,
      level,
      isLeaf: !hasChildren,
      isHeader: hasChildren,
      children: category.children || [],
    });

    if (hasChildren) {
      result = result.concat(flattenCategories(category.children, fullPath, level + 1));
    }
  });
  return result;
}

/**
 * Prefix category paths so TM and TC trees can be merged in one grid without path collisions.
 * Keeps the " > " segment separator expected by computeVisibleCategories.
 *
 * @param {Array<object>} categoriesFlat
 * @param {"tm"|"tc"} channelTag
 */
export function tagCatalogRowsWithChannel(categoriesFlat, channelTag) {
  const label = channelTag === "tc" ? "TC" : "TM";
  return (categoriesFlat || []).map((row) => ({
    ...row,
    catalogChannel: channelTag,
    path: row.path ? `${label} · ${row.path}` : `${label} ·`,
  }));
}

export function filterCategoriesBySearch(categories, searchText) {
  if (!searchText || !Array.isArray(categories) || categories.length === 0) {
    return categories || [];
  }
  const searchLower = searchText.toLowerCase();
  return categories.filter(
    (cat) =>
      (cat.label || "").toLowerCase().includes(searchLower) ||
      (cat.path || "").toLowerCase().includes(searchLower)
  );
}

export function computeVisibleCategories(filteredCategories, expandedHeaders) {
  const categoryMapByPath = new Map();
  filteredCategories.forEach((cat) => {
    categoryMapByPath.set(cat.path, cat);
  });
  const childrenMapByParentPath = new Map();
  filteredCategories.forEach((cat) => {
    if (!cat.isHeader) {
      const pathParts = cat.path.split(" > ");
      if (pathParts.length > 1) {
        const parentPath = pathParts.slice(0, -1).join(" > ");
        if (!childrenMapByParentPath.has(parentPath)) {
          childrenMapByParentPath.set(parentPath, []);
        }
        childrenMapByParentPath.get(parentPath).push(cat);
      }
    }
  });
  const result = [];
  const processedIds = new Set();
  filteredCategories.forEach((cat) => {
    if (cat.isHeader) {
      if (!processedIds.has(cat.id)) {
        result.push(cat);
        processedIds.add(cat.id);
        if (expandedHeaders.has(String(cat.id))) {
          const children = childrenMapByParentPath.get(cat.path) || [];
          children.forEach((child) => {
            if (!processedIds.has(child.id)) {
              result.push(child);
              processedIds.add(child.id);
            }
          });
        }
      }
    } else {
      const pathParts = cat.path.split(" > ");
      const parentPath = pathParts.length > 1 ? pathParts.slice(0, -1).join(" > ") : null;
      const parent = parentPath ? categoryMapByPath.get(parentPath) : null;
      if (!parent) {
        if (!processedIds.has(cat.id)) {
          result.push(cat);
          processedIds.add(cat.id);
        }
      } else if (expandedHeaders.has(String(parent.id)) && !processedIds.has(cat.id)) {
        result.push(cat);
        processedIds.add(cat.id);
      }
    }
  });
  return result;
}

const CURRENCY_SYMBOLS = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  CAD: "C$",
  AUD: "A$",
  CHF: "CHF",
  CNY: "¥",
  INR: "₹",
  MAD: "DH",
  TND: "DT",
  TRY: "₺",
};

export function getClinicPriceCurrencySymbol(currency) {
  if (!currency || typeof currency !== "string") return currency;
  return CURRENCY_SYMBOLS[currency] || currency;
}

export const CLINIC_PRICE_GRID_CURRENCY_OPTIONS = [
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "MAD", label: "MAD (DH)" },
  { value: "TND", label: "TND (DT)" },
  { value: "TRY", label: "TRY (₺)" },
];

/**
 * Options + value for react-select for the clinic's single default currency (TM + TC grid).
 * If the clinic already has a code outside the standard grid list, it is appended so the control stays controlled.
 *
 * @param {string | null | undefined} currentCode
 * @param {{ emptyDefault?: string }} [opts]
 * @returns {{ options: Array<{ value: string, label: string }>, value: { value: string, label: string } }}
 */
export function resolveClinicPriceCurrencySelectProps(currentCode, opts = {}) {
  const emptyDefault = opts.emptyDefault || "EUR";
  const base = [...CLINIC_PRICE_GRID_CURRENCY_OPTIONS];
  const raw =
    currentCode != null && String(currentCode).trim() !== ""
      ? String(currentCode).trim().toUpperCase()
      : emptyDefault;
  const inList = base.some((o) => o.value === raw);
  const options = inList
    ? base
    : [...base, { value: raw, label: `${raw} (${getClinicPriceCurrencySymbol(raw)})` }];
  const value = options.find((o) => o.value === raw) || base.find((o) => o.value === emptyDefault) || base[0];
  return { options, value };
}
